import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItemButton, ListItemText, ListItemIcon,
  CircularProgress, Alert, Stack, TextField, Typography,
  FormControl, InputLabel, Select, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, Checkbox, FormControlLabel, Box,
  InputAdornment,
  TablePagination,
} from "@mui/material";

// Services
import {
  getMantenimientoDetalle,
  getFallasModem,
  getModemsDisponibles,
  getChipsDisponibles,
  postReemplazoModem,
  postDevolucionModem,
  postReemplazoChip,
  postAgregarChip,
  postDevolucionChip,
  postRegistrarMantenimiento,
  postFinalizarMantenimiento,
} from "../../services/mantenimiento";
import { getProfile } from "../../services/auth";

// Icons
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SimCardIcon from "@mui/icons-material/SimCard";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SimCardAlertIcon from "@mui/icons-material/SimCardAlert";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import CentroEquipoDetalleDialog from "../CentroEquipoDetalleDialog";

// Vector de sucursales (incluye el índice 0 especial para La Paz)
const VEC_SUCURSALES = [
  "La Paz(El Alto)", // 0 (solo visible si el usuario es de La Paz)
  "La Paz",
  "Cochabamba",
  "Santa Cruz",
  "Oruro",
  "Chuquisaca",
  "Pando",
  "Beni",
  "Tarija",
  "Potosi",
];

// ---- Sub-modal contextual ----
function SubActionDialog({
  open, onClose, action, cod, numSim, numeroChip, estadoPendiente, onConfirm,
}) {

  // Campos comunes
  const [estado, setEstado] = useState("");
  const [sucursal, setSucursal] = useState(null);
  const [observacion, setObservacion] = useState("");

  // Fallas
  const [fallas, setFallas] = useState([]);
  const [fallasSel, setFallasSel] = useState([]);
  const [fallasLoading, setFallasLoading] = useState(false);

  // Reemplazo de Módem
  const [alias, setAlias] = useState("");
  const [grupo, setGrupo] = useState("");
  const [transportista, setTransportista] = useState("");
  const [sociedad, setSociedad] = useState("");
  const [lugar, setLugar] = useState("");
  const [modems, setModems] = useState([]);
  const [modemsTotal, setModemsTotal] = useState(0);
  const [modemSel, setModemSel] = useState(null);
  const [modemsQuery, setModemsQuery] = useState("");
  const [modemsQueryApplied, setModemsQueryApplied] = useState("");
  const [modemsPage, setModemsPage] = useState(0);
  const [modemsRowsPerPage, setModemsRowsPerPage] = useState(10);
  const [modemsLoading, setModemsLoading] = useState(false);

  // Reemplazo de Chip
  const [chips, setChips] = useState([]);
  const [chipsTotal, setChipsTotal] = useState(0);
  const [chipSel, setChipSel] = useState(null);
  const [chipsQuery, setChipsQuery] = useState("");
  const [chipsQueryApplied, setChipsQueryApplied] = useState("");
  const [chipsPage, setChipsPage] = useState(0);
  const [chipsRowsPerPage, setChipsRowsPerPage] = useState(10);
  const [chipsLoading, setChipsLoading] = useState(false);

  const isReemplazoModem = action === "Remplazo de Modem";
  const isDejarModemInactivo = action === "Dejar Modem Inactivo";
  const isReemplazoChip = action === "Remplazo de Chip";
  const isAgregarChip = action === "Agregar Chip";
  const isDejarChipInactivo = action === "Dejar Chip Inactivo";
  const isFinalizarEquipo = action === "Finalizar Mantenimiento de este Equipo";
  const isFinalizarSimple = action === "Finalizar Mantenimiento";

  const [confirmarFinalizar, setConfirmarFinalizar] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [imagen, setImagen] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (action === "Remplazo de Modem") setEstado("Mantenimiento");
    else if (action === "Remplazo de Chip") setEstado("Inactivo");
    else setEstado("");
  }, [open, action]);

  // Perfil → sucursal por defecto
  useEffect(() => {
    if (!open) return;
  
    let cancel = false;
  
    // ✅ reset al abrir
    setSucursal(null);
  
    (async () => {
      try {
        const profile = await getProfile(); 
        if (cancel) return;
  
        const suc = (profile?.sucursal || "La Paz").trim();
        const defaultSuc = suc === "La Paz" ? "La Paz(El Alto)" : suc;
  
        setSucursal(defaultSuc);
      } catch {
        if (cancel) return;
        setSucursal("La Paz(El Alto)");
      }
    })();
  
    return () => { cancel = true; };
  }, [open]);

  // Fallas (Reemplazo de Módem / Finalizar)
  useEffect(() => {
    let isMounted = true;
  
    const fetchFallas = async () => {
      // 1. Regla de Page: Validar condiciones de activación antes de pedir datos
      if (!(isReemplazoModem || isFinalizarEquipo)) {
        setFallas([]);
        return;
      }
  
      setFallasLoading(true);
      try {
        // 2. Regla de Service: Llamada directa, el service devuelve el response de axios
        const response = await getFallasModem();
  
        // 3. Ajuste de Interceptor: Si el interceptor aplana, la lista está en response.data
        const rawData = response?.data;
  
        if (isMounted) {
          // 4. Seguridad: Siempre asegurar que el estado sea un Array para evitar errores de .map()
          setFallas(Array.isArray(rawData) ? rawData : []);
        }
      } catch (err) {
        // 5. Regla de Form/Page: Manejar el error localmente para dar feedback o resetear
        console.error("Error cargando catálogo de fallas:", err);
        if (isMounted) setFallas([]);
      } finally {
        if (isMounted) setFallasLoading(false);
      }
    };
  
    fetchFallas();
  
    return () => {
      isMounted = false;
    };
  }, [isReemplazoModem, isFinalizarEquipo]);

  // Debounce de la búsqueda de modems: escribe libre, la consulta al backend
  // recién sale 350ms después de dejar de tipear (y vuelve a página 1).
  useEffect(() => {
    const t = setTimeout(() => {
      setModemsQueryApplied(modemsQuery.trim());
      setModemsPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [modemsQuery]);

  // Modems disponibles — paginado en backend (antes traía todo de una, ~1300
  // filas de golpe al frontend).
  useEffect(() => {
    let isMounted = true;

    const fetchModems = async () => {
      if (!isReemplazoModem) {
        setModems([]);
        setModemsTotal(0);
        return;
      }

      setModemsLoading(true);
      try {
        const { data } = await getModemsDisponibles({
          params: { buscar: modemsQueryApplied, page: modemsPage + 1, pageSize: modemsRowsPerPage },
        });
        if (isMounted) {
          setModems(Array.isArray(data?.items) ? data.items : []);
          setModemsTotal(Number(data?.total || 0));
        }
      } catch (err) {
        console.error("Error cargando módems disponibles:", err);
        if (isMounted) {
          setModems([]);
          setModemsTotal(0);
        }
      } finally {
        if (isMounted) {
          setModemsLoading(false);
        }
      }
    };

    fetchModems();

    return () => {
      isMounted = false;
    };
  }, [isReemplazoModem, modemsPage, modemsRowsPerPage, modemsQueryApplied]);

  // Debounce de la búsqueda de chips (mismo criterio que modems).
  useEffect(() => {
    const t = setTimeout(() => {
      setChipsQueryApplied(chipsQuery.trim());
      setChipsPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [chipsQuery]);

  // Chips disponibles — paginado en backend.
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!isReemplazoChip && !isAgregarChip) {
        setChips([]);
        setChipsTotal(0);
        return;
      }
      setChipsLoading(true);
      try {
        const { data } = await getChipsDisponibles({
          params: { buscar: chipsQueryApplied, page: chipsPage + 1, pageSize: chipsRowsPerPage },
        });
        if (!cancel) {
          setChips(Array.isArray(data?.items) ? data.items : []);
          setChipsTotal(Number(data?.total || 0));
        }
      } catch {
        if (!cancel) {
          setChips([]);
          setChipsTotal(0);
        }
      } finally {
        if (!cancel) setChipsLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [isReemplazoChip, isAgregarChip, chipsPage, chipsRowsPerPage, chipsQueryApplied]);

  useEffect(() => {
    if (!open) {
      setConfirmarFinalizar(false);
    }
  }, [open]);

  // Sucursales visibles
  const sucursalesOptions = useMemo(() => VEC_SUCURSALES, []);

  const toggleFalla = (id_falla) => {
    setFallasSel((prev) =>
      prev.includes(id_falla) ? prev.filter((id) => id !== id_falla) : [...prev, id_falla]
    );
  };

  // Radio-like control minimalista (para no importar Radio de MUI si quieres un look simple)
  function RadioLike({ name, checked, onChange, label }) {
    return (
      <FormControlLabel
        control={<Checkbox size="small" checked={checked} onChange={onChange} />}
        label={label ?? ""}
        componentsProps={{ typography: { sx: { display: "none" } } }}
      />
    );
  }

  const handleConfirm = async () => {
    // Construye falla como ["id,descripcion", ...] si corresponde
    const fallaStrings =
      (isReemplazoModem || isFinalizarEquipo)
        ? fallasSel.map((id) => {
            const f = fallas.find((x) => x.id_falla === id);
            return f ? `${f.id_falla},${f.descripcion}` : `${id},`;
          })
        : undefined;
  
    try {
      setSubmitting(true);
  
      if (isReemplazoModem) {
        // POST /req-mantenimiento/reemplazo-modem
        const payload = {
          cod: String(cod),
          num_sim: String(numSim),          // viejo
          num_sim_sec: String(modemSel),    // nuevo (radio elegido)
          estado,                           // "Inactivo" | "Mantenimiento" | "Baja"
          grupo: grupo || undefined,
          alias: alias || undefined,
          transportista: transportista || undefined,
          sociedad: sociedad || undefined,
          sucursal: sucursal || undefined,
          obs: observacion || undefined,
          lugar,                            // requerido por tu UI
          falla: fallaStrings,              // ["1,No reporta", ...]
          imagen: imagen || undefined,
        };
        const { data: resp } = await postReemplazoModem(payload);
        onConfirm?.({ action, payload, response: resp });
      }
  
      else if (isDejarModemInactivo) {
        // POST /req-mantenimiento/devolucion-modem (sin nuevo módem)
        const payload = {
          cod: String(cod),
          num_sim: String(numSim),          // viejo
          estado: "Inactivo",               // según tu UI para "dejar inactivo"
          grupo: grupo || undefined,        // opcional (si quieres enviar)
          alias: alias || undefined,        // opcional
          obs: observacion || undefined,
          lugar: lugar || undefined,
          // Nota: num_sim_sec OMITIDO a propósito
          // También omito sucursal porque tu ejemplo no lo incluye;
          // si quieres guardarla en backend, avísame y la incluimos.
        };
        const { data: resp } = await postDevolucionModem(payload);
        onConfirm?.({ action, payload, response: resp });
      }
  
      else if (isReemplazoChip) {
        // POST /req-mantenimiento/reemplazo-chip
        const payload = {
          cod: String(cod),
          numero: String(numeroChip),       // chip viejo
          numero_sec: String(chipSel),      // chip nuevo (radio elegido)
          num_sim: String(numSim),          // módem relacionado
          estado,                         // estado final del chip viejo
          sucursal: sucursal || undefined,                           
          obs: observacion || undefined,
        };
        const { data: resp } = await postReemplazoChip(payload);
        onConfirm?.({ action, payload, response: resp });
      }

      else if (isAgregarChip) {
        // POST /req-mantenimiento/agregar-chip
        const payload = {
          cod: String(cod),
          numero_sec: String(chipSel),      // chip nuevo (elegido)
          num_sim: String(numSim),          // módem al que se asigna
          sucursal: sucursal || undefined,
          obs: observacion || undefined,
        };
        const { data: resp } = await postAgregarChip(payload);
        onConfirm?.({ action, payload, response: resp });
      }

      else if (isDejarChipInactivo) {
        // POST /req-mantenimiento/devolucion-chip
        // FIX: num_sim lo resuelve el backend desde equipo_chip.relacion
        const payload = {
          cod:      String(cod),
          numero:   String(numeroChip),
          estado:   "Inactivo",
          sucursal: sucursal || undefined,
          obs:      observacion || undefined,
        };
        const { data: resp } = await postDevolucionChip(payload);
        onConfirm?.({ action, payload, response: resp });
      }
  
      else if (isFinalizarEquipo) {
        // POST /api/req-mantenimiento/registrar-mantenimiento (con fallas y observación)
        const payload = {
          cod: String(cod),
          num_sim: String(numSim),
          obs: observacion || "",
          falla: fallaStrings || [],
        };
        const { data: resp } = await postRegistrarMantenimiento(payload);
        onConfirm?.({ action, payload, response: resp });
      }
      else if (isFinalizarSimple) {
        // POST /api/req-mantenimiento/finalizar-mantenimiento (simple)
        const payload = {
          cod: String(cod),
          num_sim: String(numSim),
        };
        const { data: resp } = await postFinalizarMantenimiento(payload);
        onConfirm?.({ action, payload, response: resp });
      }
  
      // Cierra sub-modal y (si quieres) el modal padre desde el contenedor
      onClose?.();
    } catch (e) {
      console.error("[SubActionDialog] error en POST", e);
      // Puedes agregar aquí un setError o un toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{action} — {numSim}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {(isDejarModemInactivo || isDejarChipInactivo) && (
            <Alert icon={<WarningAmberIcon />} severity="warning">
              No se notificará esta operación al departamento de Sistemas.
            </Alert>
          )}

          {(isReemplazoModem || isReemplazoChip) && (
            <FormControl size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
                <MenuItem value="Inactivo">Inactivo</MenuItem>
                <MenuItem value="Mantenimiento">Mantenimiento</MenuItem>
                <MenuItem value="Baja">Baja</MenuItem>
              </Select>
            </FormControl>
          )}

          {(isReemplazoModem || isDejarModemInactivo || isReemplazoChip || isAgregarChip || isDejarChipInactivo) && (
            <FormControl size="small" fullWidth>
              <InputLabel>Sucursal a dejar</InputLabel>
              <Select
                label="Sucursal a dejar"
                value={sucursal ?? ""}
                displayEmpty
                onChange={(e) => setSucursal(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Seleccione sucursal...
                </MenuItem>
              
                {sucursalesOptions.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* FALLAS */}
          {(isReemplazoModem || isFinalizarEquipo) && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Seleccione la(s) falla(s)</Typography>
              {fallasLoading ? (
                <Stack alignItems="center" py={1}><CircularProgress size={22} /></Stack>
              ) : fallas.length > 0 ? (
                <Stack spacing={0.5}>
                  {fallas.map((f) => (
                    <FormControlLabel
                      key={f.id_falla}
                      control={
                        <Checkbox
                          size="small"
                          checked={fallasSel.includes(f.id_falla)}
                          onChange={() => toggleFalla(f.id_falla)}
                        />
                      }
                      label={f.descripcion}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No existen tipos de Falla registradas
                </Typography>
              )}
            </Box>
          )}

          {/* OBSERVACIÓN */}
          {(isReemplazoModem || isDejarModemInactivo || isReemplazoChip || isAgregarChip || isDejarChipInactivo || isFinalizarEquipo) && (
            <TextField
              size="small"
              label="Observación"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              multiline minRows={2} fullWidth required
            />
          )}

          {/* Confirmación para "Finalizar Mantenimiento" simple */}
          {isFinalizarSimple && (
            <Stack spacing={1}>
              <Alert severity="warning">
                Esta acción marcará el equipo <strong>{numSim}</strong> como <strong>mantenimiento finalizado</strong>.
                No se solicitarán más datos.
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={confirmarFinalizar}
                    onChange={(e) => setConfirmarFinalizar(e.target.checked)}
                  />
                }
                label="Sí, finalizar el mantenimiento de este equipo"
              />
            </Stack>
          )}

          {/* REEMPLAZO DE MÓDEM */}
          {isReemplazoModem && (
            <>
              <Typography variant="h6" color="error">Reemplazo</Typography>
              <TextField size="small" label="Alias para el Nuevo Modem" value={alias} onChange={(e) => setAlias(e.target.value)} fullWidth />
              <TextField size="small" label="Grupo" value={grupo} onChange={(e) => setGrupo(e.target.value)} fullWidth />
              <TextField size="small" label="Transportista" value={transportista} onChange={(e) => setTransportista(e.target.value)} fullWidth />
              <TextField size="small" label="Sociedad" value={sociedad} onChange={(e) => setSociedad(e.target.value)} fullWidth />
              <TextField size="small" label="Lugar de Instalación" value={lugar} onChange={(e) => setLugar(e.target.value)} fullWidth />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Foto del reemplazo
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Seleccionar imagen
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setImagen(e.target.files?.[0] || null)}
                  />
                </Button>
                {imagen && (
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    {imagen.name}
                  </Typography>
                )}
              </Box>
              <TextField
                size="small"
                placeholder="Buscar módem (ID, serie, modelo, estado)"
                value={modemsQuery}
                onChange={(e) => setModemsQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth sx={{ mt: 1 }}
              />

              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                Modems disponibles — {modemsLoading ? "--" : modemsTotal}
              </Typography>

              {modemsLoading ? (
                <Stack alignItems="center" py={2}><CircularProgress size={24} /></Stack>
              ) : modems.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No hay módems disponibles</Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Id</TableCell>
                        <TableCell>Numero Serie</TableCell>
                        <TableCell>Modelo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="center">Elegir</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modems.map((m, i) => (
                        <TableRow
                          key={m.num_sim || i}
                          sx={{ backgroundColor: m.estado === "Programado" ? "action.hover" : "inherit" }}
                        >
                          <TableCell>{m.id}</TableCell>
                          <TableCell>{m.num_sim}</TableCell>
                          <TableCell>{m.modelo}</TableCell>
                          <TableCell>{m.estado}</TableCell>
                          <TableCell align="center">
                            <RadioLike
                              name="modemSel"
                              checked={modemSel === m.num_sim}
                              onChange={() => setModemSel(m.num_sim)}
                              label={null}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={modemsTotal}
                    page={modemsPage}
                    onPageChange={(_, p) => setModemsPage(p)}
                    rowsPerPage={modemsRowsPerPage}
                    onRowsPerPageChange={(e) => { setModemsRowsPerPage(parseInt(e.target.value, 10)); setModemsPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />
                </>
              )}
            </>
          )}

          {/* REEMPLAZO DE CHIP / AGREGAR CHIP */}
          {(isReemplazoChip || isAgregarChip) && (
            <>
              <TextField
                size="small"
                placeholder="Buscar chip (número, modelo, ICCID)"
                value={chipsQuery}
                onChange={(e) => setChipsQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Cantidad de Chips: {chipsLoading ? "--" : chipsTotal}
              </Typography>

              {chipsLoading ? (
                <Stack alignItems="center" py={2}><CircularProgress size={24} /></Stack>
              ) : chips.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No hay chips disponibles</Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Número</TableCell>
                        <TableCell>Modelo</TableCell>
                        <TableCell align="center">Elegir</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chips.map((c, i) => (
                        <TableRow key={c.serie || i} sx={{ backgroundColor: "action.hover" }}>
                          <TableCell>{c.serie}</TableCell>
                          <TableCell>{c.modelo}</TableCell>
                          <TableCell align="center">
                            <RadioLike
                              name="chipSel"
                              checked={chipSel === c.serie}
                              onChange={() => setChipSel(c.serie)}
                              label={null}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={chipsTotal}
                    page={chipsPage}
                    onPageChange={(_, p) => setChipsPage(p)}
                    rowsPerPage={chipsRowsPerPage}
                    onRowsPerPageChange={(e) => { setChipsRowsPerPage(parseInt(e.target.value, 10)); setChipsPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={
            submitting ||
            (isReemplazoModem && (!estado || !sucursal || !modemSel)) ||
            (isDejarModemInactivo && (!observacion)) ||
            (isReemplazoChip && (!estado || !sucursal || !chipSel)) ||
            (isAgregarChip && (!sucursal || !chipSel)) ||
            (isDejarChipInactivo && (!observacion)) ||
            (isFinalizarEquipo && (fallasSel.length === 0 || !observacion)) ||
            (isFinalizarSimple && !confirmarFinalizar)
          }
        >
          {submitting ? <CircularProgress size={18} /> : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- Modal principal (acciones) ----
export default function AsignadoModal({ open, onClose, cod, numSim, onSelect, onRefreshRequerimiento }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [estadoPendiente, setEstadoPendiente] = useState(null);
  const [numeroChip, setNumeroChip] = useState(null);
  const [centroEquipo, setCentroEquipo] = useState(null);

  const openCentroEquipo = (identificador, tipo = "modem") => {
    const value = identificador == null ? "" : String(identificador).trim();
    if (!value) return;
    setCentroEquipo({ tipo, identificador: value });
  };

  // Sub-modal state
  const [subOpen, setSubOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const canFetch = useMemo(() => Boolean(open && cod && numSim), [open, cod, numSim]);

  useEffect(() => {
    let cancel = false;
    const fetchDetalle = async () => {
      if (!canFetch) return;
      setLoading(true);
      setErr(null);
      try {
        const { data } = await getMantenimientoDetalle(cod, numSim);
        if (cancel) return;
        // FIX: el interceptor ya aplana { success, data } → data es el objeto directo
        const info = data || {};
        setEstadoPendiente(info?.estado_pendiente ?? null);
        setNumeroChip(info?.numero_chip ?? null);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.error || e.message || "Error al cargar acciones");
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchDetalle();
    return () => { cancel = true; };
  }, [canFetch, cod, numSim]);

  const opciones = useMemo(() => {
    const items = [];
    if (estadoPendiente === "Remplazo Adicion") {
      if (numeroChip) {
        items.push("Remplazo de Chip");
        items.push("Dejar Chip Inactivo");
      } else {
        items.push("Agregar Chip");
      }
       items.push("Finalizar Mantenimiento");
    } else {
      items.push("Remplazo de Modem");
      items.push("Dejar Modem Inactivo");
      if (numeroChip) {
        items.push("Remplazo de Chip");
        items.push("Dejar Chip Inactivo");
      } else {
        items.push("Agregar Chip");
      }
      items.push("Finalizar Mantenimiento de este Equipo");
    }
    return items;
  }, [estadoPendiente, numeroChip]);

  const getIconFor = (action) => {
    switch (action) {
      case "Remplazo de Modem": return <SwapHorizIcon />;
      case "Dejar Modem Inactivo": return <PowerSettingsNewIcon />;
      case "Remplazo de Chip": return <SimCardIcon />;
      case "Agregar Chip": return <AddCircleOutlineIcon />;
      case "Dejar Chip Inactivo": return <SimCardAlertIcon />;
      case "Finalizar Mantenimiento de este Equipo": return <CheckCircleOutlineIcon />;
      case "Finalizar Mantenimiento": return <CheckCircleOutlineIcon />;
      default: return null;
    }
  };

  const handleOpenSub = (action) => {
    if (action === "Finalizar Mantenimiento") {
      setSelectedAction(action);
      setConfirmChecked(false);
      setConfirmOpen(true);
      return;
    }
    setSelectedAction(action);
    setSubOpen(true);
 };
  const handleCloseSub = () => {
    setSubOpen(false);
    setSelectedAction(null);
  };
  const handleConfirmSub = async (payload) => {
    try {
      if (onSelect) onSelect(selectedAction, payload);
      else console.log("[AsignadoModal] acción confirmada:", selectedAction, payload);
      // 🔄 refrescar detalle/reload desde Programacion.jsx si fue provisto
      if (typeof onRefreshRequerimiento === "function") {
        await onRefreshRequerimiento();
      }
    } finally {
      handleCloseSub();
      onClose?.();
    }
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setSelectedAction(null);
  };

  const handleConfirmFinish = async () => {
    try {
      setConfirming(true);
      // POST simple: finalizar mantenimiento
      const payload = { cod: String(cod), num_sim: String(numSim) };
      const { data: resp } = await postFinalizarMantenimiento(payload);
  
      // Opcional: notificación / console
      console.log("[AsignadoModal] Finalizar Mantenimiento OK:", resp);
  
      // refresco externo (si te lo pasaron)
      if (typeof onRefreshRequerimiento === "function") {
        await onRefreshRequerimiento();
      }
    } catch (e) {
      console.error("[AsignadoModal] error al finalizar mantenimiento", e);
    } finally {
      setConfirming(false);
      setConfirmOpen(false);
      setSelectedAction(null);
      onClose?.(); // cierra el modal de acciones
    }
  };



  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Acciones —{" "}
          {numSim ? (
            <Box
              component="span"
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
              }}
              onClick={() => openCentroEquipo(numSim)}
            >
              {numSim}
            </Box>
          ) : (
            "—"
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Stack alignItems="center" py={1.5}>
              <CircularProgress size={26} />
            </Stack>
          )}
          {!loading && err && <Alert severity="error">{err}</Alert>}
          {!loading && !err && (
            <List dense disablePadding>
              {opciones.map((opt) => (
                <ListItemButton key={opt} onClick={() => handleOpenSub(opt)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>{getIconFor(opt)}</ListItemIcon>
                  <ListItemText primary={opt} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {selectedAction && (
        <SubActionDialog
          open={subOpen}
          onClose={handleCloseSub}
          action={selectedAction}
          cod={cod}
          numSim={numSim}
          numeroChip={numeroChip}
          estadoPendiente={estadoPendiente}
          onConfirm={handleConfirmSub}
        />
      )}

      <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Finalizar Mantenimiento — {numSim}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="warning">
              Esta acción marcará el equipo <strong>{numSim}</strong> como <strong>mantenimiento finalizado</strong>.
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
              }
              label="Sí, quiero finalizar el mantenimiento"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmFinish}
            disabled={!confirmChecked || confirming}
          >
            {confirming ? <CircularProgress size={18} /> : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo={centroEquipo?.tipo || "modem"}
        identificador={centroEquipo?.identificador}
        onClose={() => setCentroEquipo(null)}
      />
    </>
  );
}
